import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

const contactCards = [
  { icon: 'ri-mail-line', title: '邮箱联系', value: 'contact@heihei-comic.example.com' },
  { icon: 'ri-time-line', title: '反馈时效', value: '工作日 48 小时内回复' },
  { icon: 'ri-feedback-line', title: '建议通道', value: '欢迎推荐暗黑向作品' },
];

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string) || '';
    if (honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/da20vi5v7gurs51ef30g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam = /spam/i.test(String(serverMsg));

      if (res.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(isSpam || parsed == null ? '提交失败，请稍后重试或换个联系方式。' : String(serverMsg));
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后再试。');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: '联系我们 - 嘿嘿漫画网',
            url: 'https://dailycartoon.github.io/contact',
          }),
        }}
      />

      <Header />

      <main>
        <section className="border-b border-background-200/60 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
            <nav aria-label="面包屑" className="mb-4 text-xs text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="transition-colors hover:text-primary-400">首页</Link></li>
                <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
                <li className="text-foreground-400">联系我们</li>
              </ol>
            </nav>
            <h1 className="font-heading text-3xl text-foreground-950">联系我们</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              无论是作品推荐、深夜档追更建议，还是对重口味暗黑题材的独到见解，都欢迎通过下方表单告诉我们。我们重视每一位读者的声音。
            </p>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-12 md:px-6 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-2">
            {contactCards.map((c) => (
              <div key={c.title} className="flex items-center gap-4 rounded-xl border border-background-200/60 bg-background-100 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-accent-400">
                  <i className={`${c.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <div>
                  <div className="text-sm font-bold text-foreground-950">{c.title}</div>
                  <div className="mt-0.5 text-sm text-foreground-600">{c.value}</div>
                </div>
              </div>
            ))}
            <div className="rounded-xl border border-primary-400/40 bg-primary-500/10 p-5">
              <div className="flex items-center gap-2 text-sm font-bold text-primary-300">
                <i className="ri-sparkling-2-line" aria-hidden="true"></i>
                追更小贴士
              </div>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                想第一时间收到深夜档更新？先前往对应专区收藏你喜欢的漫画，再顺便告诉我们你想追的题材，编辑部会优先安排。
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-background-200/60 bg-background-100 p-6 lg:col-span-3">
            <h2 className="font-heading text-xl text-foreground-950">在线留言</h2>
            <p className="mt-1 text-sm text-foreground-600">留下你的意见或作品推荐，我们会在 48 小时内处理。</p>

            <form onSubmit={handleSubmit} data-readdy-form className="mt-6 space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm text-foreground-700">你的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300/60 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm text-foreground-700">联系邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="name@example.com"
                    className="w-full rounded-md border border-background-300/60 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="mb-1.5 block text-sm text-foreground-700">留言主题</label>
                <select
                  id="subject"
                  name="subject"
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
                >
                  <option value="作品推荐">作品推荐</option>
                  <option value="追更建议">追更建议</option>
                  <option value="反馈问题">反馈问题</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm text-foreground-700">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="想推荐哪部重口味暗黑向作品？或是对深夜档有什么建议？"
                  className="w-full resize-none rounded-md border border-background-300/60 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
              </div>

              <div className="hp-field" aria-hidden="true">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status === 'success' && (
                <p className="rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-950">
                  已收到你的留言，感谢反馈！我们会尽快与你联系。
                </p>
              )}
              {status === 'error' && (
                <p className="rounded-md border border-primary-400/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-300">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
              >
                提交留言 <i className="ri-send-plane-line" aria-hidden="true"></i>
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}